<template>
    <!-- Success message -->
    <div v-if="success" class="d-flex flex-column">
        <p class="font-body text-muted mb-0 text-start">
            {{ $t('contactUs.requestForm.success.thankYou') }}
        </p>
        <p class="font-body text-muted mb-0 text-start">
            {{
                $t('contactUs.requestForm.success.requestSent', {
                    requestType: localizedRequestType,
                })
            }}
        </p>
    </div>
    <!-- Form -->
    <form
        v-else
        @submit.prevent="submitForm"
        class="d-flex flex-column main-container"
    >
        <!-- Form description -->
        <p class="font-body text-muted mb-0">
            {{
                requestType === 'email'
                    ? $t('contactUs.requestForm.descriptions.email')
                    : $t('contactUs.requestForm.descriptions.other', {
                          requestType: localizedRequestType,
                      })
            }}
        </p>

        <!-- Fields and form elements -->
        <div class="d-flex flex-column form-container">
            <Field
                :label="$t('contactUs.requestForm.fields.name.label')"
                v-model="formData.name"
                type="text"
                required
                :disabled="user.name !== ''"
            />
            <Field
                :label="$t('contactUs.requestForm.fields.company.label')"
                v-model="formData.company"
                type="text"
                required
                :disabled="user.company !== ''"
            />
            <Field
                v-if="requestType !== 'phone'"
                type="email"
                :label="$t('contactUs.requestForm.fields.email.label')"
                v-model="formData.email"
                required
            />
            <Field
                v-if="requestType !== 'email'"
                type="tel"
                :label="$t('contactUs.requestForm.fields.phone.label')"
                v-model="formData.phone"
                required
            />
            <Field
                :label="$t('contactUs.requestForm.fields.category.label')"
                v-model="formData.category"
                type="select"
                :params="{
                    options: categories,
                }"
                required
            />
            <Field
                :label="$t('contactUs.requestForm.fields.message.label')"
                v-model="formData.message"
                type="textarea"
                required
                :params="{
                    minlength: 20,
                    placeholder: $t(
                        'contactUs.requestForm.fields.message.placeholder'
                    ),
                }"
                :invalidMessage="
                    $t('contactUs.requestForm.fields.message.validation')
                "
            />
            <div
                v-if="requestType !== 'email'"
                class="d-flex flex-column dates-container"
            >
                <div
                    v-for="(date, index) in formData.days_available"
                    :key="index"
                >
                    <Field
                        :label="
                            index === 0
                                ? $t(
                                      'contactUs.requestForm.fields.availability.label'
                                  )
                                : $t(
                                      'contactUs.requestForm.fields.nextPreferredDate.label'
                                  )
                        "
                        v-model="formData.days_available[index]"
                        type="date"
                        :required="index === 0"
                    />
                </div>
                <button
                    :disabled="
                        formData.days_available[
                            formData.days_available.length - 1
                        ] === ''
                    "
                    type="button"
                    class="btn btn-primary align-self-end"
                    @click="addAvailability"
                >
                    {{ $t('contactUs.requestForm.actions.addAnotherDate') }}
                    <i class="fi fi-add"></i>
                </button>
            </div>

            <Field
                :label="$t('contactUs.requestForm.fields.sendCopy.label')"
                v-model="formData.send_copy"
                type="checkbox"
            />
        </div>
        <div
            class="d-flex flex-column justify-content-center"
            style="gap: var(--space-4)"
        >
            <div class="divider" />
            <button type="submit" class="btn btn-secondary w-100">
                {{ $t('common.actions.send') }}
            </button>
        </div>
    </form>
</template>

<script>
import Field from '../Field.vue';

const CATEGORIES = [
    {
        labelKey: 'contactUs.requestForm.categories.bugIssue',
        value: 'BUG_ISSUE',
    },
    {
        labelKey: 'contactUs.requestForm.categories.featureEnhancement',
        value: 'FEATURE_ENHANCEMENT',
    },
    {
        labelKey: 'contactUs.requestForm.categories.dataConcern',
        value: 'DATA_CONCERN',
    },
    {
        labelKey: 'contactUs.requestForm.categories.questionHowTo',
        value: 'QUESTION_HOW_TO',
    },
    {
        labelKey: 'contactUs.requestForm.categories.accessPermissions',
        value: 'ACCESS_PERMISSIONS',
    },
    {
        labelKey: 'contactUs.requestForm.categories.ipacGuidanceSupport',
        value: 'IPAC_GUIDANCE_SUPPORT',
        localizeTerminology: true,
    },
    {
        labelKey: 'contactUs.requestForm.categories.generalFeedback',
        value: 'GENERAL_FEEDBACK',
    },
];

export default {
    name: 'RequestForm',
    components: {
        Field,
    },
    props: {
        /**
         * Whether the form has been successfully submitted.
         *
         * @type {Boolean}
         * @default false
         */
        success: {
            type: Boolean,
            required: true,
            default: false,
        },
        /**
         * The type of request being made.
         *
         * @type {String}
         * @default 'visit'
         * @enum {'visit' | 'email' | 'phone'}
         */
        requestType: {
            type: String,
            required: true,
            default: 'visit',
            validator: (value) => ['visit', 'email', 'phone'].includes(value),
        },
        /**
         * The user object containing user information.
         *
         * @type {Object}
         * @property {string} name - The user's name.
         * @property {string} email - The user's email.
         * @property {string} phone - The user's phone number.
         * @property {string} company - The user's company name.
         */
        user: {
            type: Object,
            required: true,
            validator: (value) => {
                return (
                    typeof value.name === 'string' &&
                    typeof value.email === 'string' &&
                    typeof value.phone === 'string' &&
                    typeof value.company === 'string'
                );
            },
        },
        /**
         * The default category to pre-select.
         *
         * @type {String}
         * @default ''
         */
        defaultCategory: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            formData: {
                name: this.user.name,
                company: this.user.company,
                email: this.requestType === 'phone' ? '' : this.user.email, // If phone form type, email is empty
                phone:
                    this.requestType === 'email'
                        ? ''
                        : this.cleanPhoneNumber(this.user.phone), // If email form type, phone is empty
                message: '',
                category: this.defaultCategory,
                days_available: this.requestType === 'email' ? [] : [''], // If email form type, days_available is empty
                send_copy: false,
            },
        };
    },
    emits: ['submit'],
    computed: {
        localizedRequestType() {
            return this.$t(
                `contactUs.requestForm.requestTypes.${this.requestType}`
            );
        },
        categories() {
            return CATEGORIES.map((category) => {
                let label = this.$t(category.labelKey);
                if (category.localizeTerminology) {
                    label = this.localizeInfectionPreventionTerminology(label);
                }
                return {
                    label,
                    value: category.value,
                };
            });
        },
    },
    watch: {
        success(newVal) {
            if (newVal) {
                // Reset form data when success becomes true to initial state
                this.formData = {
                    name: this.user.name,
                    company: this.user.company,
                    email: this.requestType === 'phone' ? '' : this.user.email,
                    phone:
                        this.requestType === 'email'
                            ? ''
                            : this.cleanPhoneNumber(this.user.phone),
                    message: '',
                    category: this.defaultCategory,
                    days_available: this.requestType === 'email' ? [] : [''],
                    send_copy: false,
                };
            }
        },
    },
    methods: {
        addAvailability() {
            const lastDate =
                this.formData.days_available[
                    this.formData.days_available.length - 1
                ];
            if (lastDate) {
                const dateObj = new Date(lastDate);
                dateObj.setDate(dateObj.getDate() + 1);
                const nextDate = dateObj.toISOString().split('T')[0];
                this.formData.days_available.push(nextDate);
            } else {
                this.formData.days_available.push('');
            }
        },
        submitForm() {
            const { name, company, ...payload } = this.formData;
            this.$emit('submit', payload);
        },

        //+1 (950) 430-2368
        cleanPhoneNumber(phone) {
            return phone.replace(/[^\d]/g, '');
        },
    },
};
</script>

<style scoped>
.main-container {
    gap: var(--space-6);
}

.form-container {
    gap: var(--space-4);
}

.dates-container {
    gap: var(--space-4);
}

.divider {
    width: 100%;
    height: 1px;
    background: var(--neutral-600);
}

@media (max-width: 767.98px) {
    .main-container {
        gap: var(--space-5);
    }
}
</style>
